import React, { useEffect, useRef, useState } from 'react';
import styles from './ProfileLogDrawer.module.css';
import Btn from '../common/Btn';
import Drawer from '../common/Drawer';
import Pill from '../common/Pill';
import { formatBytes, formatSeconds, runStateKind } from './cciStatus';
import { apiGetCciProfileLog } from '../../api/cci';

const ICO_DOWNLOAD = 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3';
const ICO_TERMINAL = 'm4 17 6-6-6-6M12 19h8';

// A profile of a runner report, with its error and the log the runner uploaded to S3
export default function ProfileLogDrawer({ jobId, profile, onClose }) {
  const [log, setLog] = useState({ loaded: false });
  const logRef = useRef(null);
  const name = profile?.name;
  const logUrl = profile?.log_url;

  useEffect(() => {
    if (!name || !logUrl) return undefined;
    let cancelled = false;
    setLog({ loaded: false });
    apiGetCciProfileLog(jobId, name)
      .then((r) => { if (!cancelled) setLog({ loaded: true, data: r }); })
      .catch((e) => { if (!cancelled) setLog({ loaded: true, error: e }); });
    return () => { cancelled = true; };
  }, [jobId, name, logUrl]);

  // Failures are at the end of a build log: start there
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [log]);

  if (!profile) return null;

  const stats = [
    { label: 'Status', value: <Pill kind={runStateKind(profile.status)}>{profile.status}</Pill> },
    { label: 'Duration', value: formatSeconds(profile.seconds) },
    { label: 'Cached', value: profile.cached ? 'Yes' : 'No' },
  ];
  if (log.data) stats.push({ label: 'Log size', value: formatBytes(log.data.size) });

  return (
    <Drawer
      open
      onClose={onClose}
      icon={ICO_TERMINAL}
      title={profile.name}
      subtitle={logUrl}
      stats={stats}
      footer={log.data && (
        <Btn variant="primary" icon={ICO_DOWNLOAD} onClick={() => window.location.assign(log.data.download_url)}>
          Download log
        </Btn>
      )}
    >
      {profile.error && <div className={styles.error}>{profile.error}</div>}
      {!logUrl && <span className={styles.muted}>No log was uploaded for this profile.</span>}
      {logUrl && !log.loaded && <span className={styles.muted}>Loading log…</span>}
      {log.error && (
        <div className={styles.error}>Could not load the log: {String(log.error.detail ?? log.error)}</div>
      )}
      {log.data && (
        <>
          {log.data.truncated && (
            <span className={styles.muted}>
              Showing the end of the log. Download it to see all {formatBytes(log.data.size)}.
            </span>
          )}
          <pre ref={logRef} className={styles.log} data-testid="profile-log">
            {log.data.content || 'The log is empty.'}
          </pre>
        </>
      )}
    </Drawer>
  );
}
